import type { EmailEvent, EmailRule } from '../types';

export type TriggerAssignment = {
  trigger: string;
  ruleId: string | null;
  templateId: string;
  enabled: boolean;
  userType: string;
};

const TRIGGERS_WITH_USER_TYPE = new Set([
  'company_otp',
  'company_welcome',
  'welcome_job_seeker',
]);

export function triggerSupportsUserType(trigger: string): boolean {
  return TRIGGERS_WITH_USER_TYPE.has(trigger);
}

/** Suggest a template id from trigger name (e.g. company_otp → company-otp). */
export function suggestTemplateId(trigger: string, templateIds: string[]): string {
  const slug = trigger.replace(/_/g, '-');
  if (templateIds.includes(slug)) return slug;
  const partial = templateIds.find((id) => id.includes(slug) || slug.includes(id));
  return partial ?? templateIds[0] ?? '';
}

export function rulesToAssignments(
  rules: EmailRule[],
  events: EmailEvent[],
  templateIds: string[]
): TriggerAssignment[] {
  return events.map((event) => {
    const forTrigger = rules
      .filter((rule) => rule.trigger === event.id)
      .sort((a, b) => a.priority - b.priority);
    const primary = forTrigger[0];
    return {
      trigger: event.id,
      ruleId: primary?.id ?? null,
      templateId: primary?.template_id || suggestTemplateId(event.id, templateIds),
      enabled: primary?.enabled ?? true,
      userType: primary?.conditions.user_type ?? '',
    };
  });
}

export function assignmentsToRules(assignments: TriggerAssignment[]): EmailRule[] {
  return assignments
    .filter((assignment) => assignment.templateId)
    .map((assignment) => ({
      id: assignment.ruleId ?? `${assignment.trigger}-routing`,
      trigger: assignment.trigger,
      template_id: assignment.templateId,
      enabled: assignment.enabled,
      priority: 0,
      conditions: assignment.userType ? { user_type: assignment.userType } : {},
    }));
}

export function countUncoveredTriggers(assignments: TriggerAssignment[]): number {
  return assignments.filter((a) => a.enabled && !a.templateId).length;
}
